import React, { useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { change } from 'redux-form';
import { CheckboxItem } from 'chameleon/ui-stack/ui/forms/check-box-list';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import { calculateClassesStats, getFormIdField } from '../../utils';
import Context from '../../Context';
import SelectAllUndo from '../SelectAllUndo';

const sortStudents = (a, b) => {
  if (a.lastName < b.lastName) {
    return -1;
  } else if (a.lastName > b.lastName) {
    return 1;
  } else if (a.firstName < b.firstName) {
    return -1;
  } else if (a.firstName > b.firstName) {
    return 1;
  }
  return 0;
};

const StudentsDropdown = ({
  students = [],
  fields,
  section,
  actualAssigneesFieldName,
  actualAssigneesList,
  indexOfActualAssignee,
  dispatch,
  meta: { form },
  allAssigneesName,
  allAssignees,
  selectedAssignment,
  disabledStudents,
  individualStudentAssignmentFlag,
  isAssign,
}) => {
  const {
    classesStats,
    dispatchClassesStats,
    isStudentsFormDirty,
    markStudentsFormAsDirty,
  } = useContext(Context);
  const allFields = fields.getAll() || [];
  const disabledStudentIds = disabledStudents.map((student) => student.id);
  const sectionId = section.sectionId.toString();
  const selectedAssignmentId =
    selectedAssignment && selectedAssignment.id
      ? selectedAssignment.id.toString()
      : null;
  const sectionDisabledStudents = disabledStudents.filter(
    (s) => s.sectionId === sectionId && s.assignmentId === selectedAssignmentId,
  );
  const sectionFormStudents = allFields.filter(
    (s) => s.sectionId === sectionId && s.assignmentId === selectedAssignmentId,
  );

  // Array.sort has issue with React since sort works in place and mutate the array
  // https://stackoverflow.com/questions/49278578/reactjs-sorting-typeerror-0-is-read-only
  const sortedStudents = students.slice().sort(sortStudents);

  const getSectionFormFieldStudents = (
    formFields,
    isFilteredByStatus = false,
  ) => {
    /*
     * This list have all the students that are enabled within the `section` when isFilteredByStatus is true
     * It's used to check how many students we have left in the form after unchecking the current field.
     * If all the students are removed from the redux-form, we should automatically remove the class also.
     * */
    const enabledSectionFormFieldStudents = (formFields.getAll() || []).filter(
      (s) =>
        s.id ===
          getFormIdField(
            s.studentId,
            section.sectionId,
            selectedAssignmentId,
          ) &&
        (isAssign
          ? !disabledStudentIds.includes(s.id)
          : isFilteredByStatus
            ? !s.status
            : true),
    );
    return enabledSectionFormFieldStudents;
  };

  const selectedStudentsCount = useRef(
    getSectionFormFieldStudents(fields).length,
  );

  const getEnabledSectionFormFieldStudents = (formFields) =>
    getSectionFormFieldStudents(formFields, true);

  const addSectionToForm = () => {
    const newActualAssigneesList = [...actualAssigneesList, section];
    dispatch(change(form, actualAssigneesFieldName, newActualAssigneesList));

    const newAllAssignees = [...allAssignees, section];
    dispatch(change(form, allAssigneesName, newAllAssignees));
  };

  const removeSectionFromForm = () => {
    const newActualAssigneesList = [
      ...actualAssigneesList.slice(0, indexOfActualAssignee),
      ...actualAssigneesList.slice(indexOfActualAssignee + 1),
    ];
    dispatch(change(form, actualAssigneesFieldName, newActualAssigneesList));

    const allAssigneeIds = allAssignees.map((assignee) => assignee.id);
    const allAssigneesSectionIndex = allAssigneeIds.indexOf(section.id);
    const newAllAssigneesList = [
      ...allAssignees.slice(0, allAssigneesSectionIndex),
      ...allAssignees.slice(allAssigneesSectionIndex + 1),
    ];
    dispatch(change(form, allAssigneesName, newAllAssigneesList));
  };

  const handleOnToggle = (
    event,
    formFields,
    formFieldIndex,
    isChecked,
    student,
  ) => {
    !isStudentsFormDirty && markStudentsFormAsDirty();
    event.preventDefault();
    let enabledSelectedStudentsLength =
      getEnabledSectionFormFieldStudents(formFields).length;
    let selectedStudentsLength = getSectionFormFieldStudents(formFields).length;
    if (isChecked) {
      formFields.remove(formFieldIndex);
      enabledSelectedStudentsLength -= 1;
      selectedStudentsLength -= 1;
      selectedStudentsCount.current = selectedStudentsLength;
    } else {
      formFields.push(student);
      enabledSelectedStudentsLength += 1;
      selectedStudentsLength += 1;
      selectedStudentsCount.current = selectedStudentsLength;
    }

    /*
     * Check if the current section is selected or not.
     * If it's selected and AT LEAST one student was unchecked, we shall uncheck the class (remove it from redux-form).
     * If it's not selected and ALL THE STUDENTS (that can be checked - are enabled) are checked,
     * we shall check the class (add it to redux-form).
     * */
    const isSectionSelected = indexOfActualAssignee >= 0;
    const enabledStudents = students.filter(
      (s) =>
        !disabledStudentIds.includes(
          getFormIdField(s.id, section.sectionId, selectedAssignmentId),
        ),
    );
    const totalStudentsLength = enabledStudents.length;

    if (
      !isSectionSelected &&
      enabledSelectedStudentsLength === totalStudentsLength
    ) {
      addSectionToForm();
    } else if (
      isSectionSelected &&
      enabledSelectedStudentsLength < totalStudentsLength
    ) {
      removeSectionFromForm();
    }

    const previousCheckStatus = !isChecked;
    calculateClassesStats({
      dispatch: dispatchClassesStats,
      step: previousCheckStatus ? 'add' : 'remove',
      stats: classesStats,
      students: [student],
    });
  };

  const getStudentProps = (student) => {
    const { id: studentId, sectionId, firstName, lastName } = student;
    const { assignmentId: sectionAssignmentId } = section;
    const assignmentId = selectedAssignmentId || sectionAssignmentId;
    const studentFormId = getFormIdField(studentId, sectionId, assignmentId);
    const formStudent = allFields.find((field) => field.id === studentFormId);
    const fieldIndex = allFields.indexOf(formStudent);
    const isDisabled = disabledStudentIds.indexOf(studentFormId) >= 0;
    const { fromOtherISA = false } = formStudent || {};
    let isChecked = fieldIndex >= 0;

    // update the student object to contain the section ids
    const studentWithSectionData = {
      ...student,
      id: studentFormId,
      studentId,
      sectionId,
    };

    if (assignmentId) {
      studentWithSectionData.assignmentId = assignmentId;
    }

    return {
      key: `${studentId}_${section.id}`,
      id: `${studentId}_${section.id}`,
      section: studentWithSectionData, // TODO refactor CheckboxItem section props to something generic
      name: `${firstName} ${lastName}`,
      isChecked,
      onToggle: handleOnToggle,
      onNameClick: handleOnToggle,
      toggleOnNameClick: true,
      isDisabled,
      fieldName: `student`,
      fields,
      fieldIndex,
      handleSelection: () => {},
      isActiveItem: false,
      isInvisible: isAssign ? isDisabled : fromOtherISA,
      color: 'blue',
      className: 'py-2',
    };
  };

  return (
    <div
      data-test-id={DATA_TEST_IDS.SECTION_STUDENTS_CONTAINER}
      className="pl-12.2 pb-2 border-b border-gray-300"
      role="group"
    >
      {!isAssign &&
        individualStudentAssignmentFlag &&
        !!sortedStudents.length && (
          <SelectAllUndo
            assignmentId={selectedAssignmentId}
            students={students}
            disabledStudentIds={sectionDisabledStudents.map(
              (student) => student.studentId,
            )}
            selectedStudents={sectionFormStudents}
            formInfo={{
              name: form,
              dispatch,
              students: allFields,
            }}
            addSectionToForm={addSectionToForm}
            removeSectionFromForm={removeSectionFromForm}
            markFormAsDirty={markStudentsFormAsDirty}
            isFormDirty={isStudentsFormDirty}
          />
        )}
      {sortedStudents.map((s) => (
        <div
          id={s.id} //for aria-activedescendant
          role="treeitem"
          tabIndex={-1}
          aria-selected={false}
          data-test-id={DATA_TEST_IDS.CLASS_DROPDOWN_STUDENT_ELEMENT}
          className="sections_student"
          key={`${section.id}_${s.id}`}
        >
          <CheckboxItem {...getStudentProps(s)} />
        </div>
      ))}
    </div>
  );
};

StudentsDropdown.propTypes = {
  selectedAssignment: PropTypes.object,
  individualStudentAssignmentFlag: PropTypes.bool,
};

StudentsDropdown.defaultProps = {
  selectedAssignment: {},
  individualStudentAssignmentFlag: false,
};

export default StudentsDropdown;
